import { useState, ChangeEvent } from 'react';

import '../TokenAndSolManagerCsv.css';
import { Connection, Keypair, PublicKey } from '@solana/web3.js';
import { useSplToken } from '../hooks/useSplToken';
import parseCsv from '../utils/parse-csv';
import { csvDownloader } from '../utils/csv-downloader';
import { customTokenAirdropExample, customTokenCollectorExample } from '../utils/csv-examples';
import { bs58 } from '@project-serum/anchor/dist/cjs/utils/bytes';
import { getMultipleAccounts } from '@solana/spl-token';
import sleep from '../utils/sleep';
import { removeKeypairDuplicates } from '../utils/remove-keypair-duplicates';
import { burnerPriorityFee, tokenManagerJitoTip } from '../../ConfigFees';
import { usePumpswap } from '../hooks/usePumpswap';

export function TokenManagerCsv({
    connection,
    deployer,
    deployedTokenMint,
    funder,
    freezer,
    allUniqueWallets,
    volumeBuyWallets,
    allWallets,
    allWalletRoles,
    logMessage,
}: {
    connection: Connection;
    deployer: Keypair;
    deployedTokenMint: string;
    funder: Keypair;
    freezer: Keypair;
    allUniqueWallets: Keypair[];
    volumeBuyWallets: Keypair[];
    allWallets: Keypair[];
    allWalletRoles: string[];
    logMessage: (message: string, type?: 'success' | 'error') => void;
}) {
    const {
        getWalletTokenAccounts,
        burnAllTokenAccountsWithJito,
        sendTokensFromAllWalletsToReceiver,
        massAirdropWithJito,
        getTokenDecimals,
        createTokenAccountsWithJito,
        thawTokenAccountsWithJito,
        getMultipleMints,
    } = useSplToken();
    const { burnAllUserVolumeAccumulators } = usePumpswap();
    const [burnConfirmation, setBurnConfirmation] = useState<boolean>(false);
    const burnConfirmationChagne = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setBurnConfirmation(checked);
    };

    const [customBurnConfirmation, setCustomBurnConfirmation] = useState<boolean>(false);
    const customBurnConfirmationChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setCustomBurnConfirmation(checked);
    };

    const [chooseTokenToBurn, chooseSetTokenToBurn] = useState<string>('');
    const chooseTokenToBurnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        chooseSetTokenToBurn(event.target.value);
    };

    function getWalletLabel(keypair: Keypair) {
        const base58Key = keypair.publicKey.toBase58();
        return `${base58Key.substring(0, 5)}...${base58Key.substring(base58Key.length - 5)}`;
    }
    const selectedWalletLabels = allWallets.map(getWalletLabel);

    const roleCounters: { [key: string]: number } = { MIX: 1, BUNDLEBUY: 1 };
    function getWalletRole(role: string) {
        const formattedRole = `${role.charAt(0).toUpperCase()}${role.slice(1).toLowerCase()}`;

        if (roleCounters.hasOwnProperty(role)) {
            return `${formattedRole}${roleCounters[role]++}`;
        } else {
            return formattedRole;
        }
    }
    const selectedWalletRoles = allWalletRoles.map(getWalletRole);

    const [walletToReceiveAllTokens, setWalletToReceiveAllTokens] = useState(0);
    const walletToReceiveAllTokensChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setWalletToReceiveAllTokens(parseInt(event.target.value, 10));
    };

    const [walletToReceiveAllFeesFromBurn, setWalletToReceiveAllFeesFromBurn] = useState(0);
    const walletToReceiveAllFeesFromBurnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setWalletToReceiveAllFeesFromBurn(parseInt(event.target.value, 10));
    };

    const [sourceWalletOfToken, setSourceWalletOfToken] = useState(0);
    const sourceWalletOfTokenChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSourceWalletOfToken(parseInt(event.target.value, 10));
    };

    const [disperseTokenCsvFile, setDisperseTokenCsvFile] = useState<File | null>(null);
    const disperseTokenCsv = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file && file.type === 'text/csv') {
            setDisperseTokenCsvFile(file);

            const fileNameLabel = document.querySelector('.disperse-token-csv-file-name');
            if (fileNameLabel) {
                fileNameLabel.textContent = file.name;
            }
        } else {
            setDisperseTokenCsvFile(null);
        }
    };

    const [customWalletToReceiveAllTokens, setCustomWalletToReceiveAllTokens] = useState(0);
    const customWalletToReceiveAllTokensChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCustomWalletToReceiveAllTokens(parseInt(event.target.value, 10));
    };

    const [collectTokenCsvFile, setCollectTokenCsvFile] = useState<File | null>(null);
    const collectTokenCsv = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file && file.type === 'text/csv') {
            setCollectTokenCsvFile(file);

            const fileNameLabel = document.querySelector('.collect-token-csv-file-name');
            if (fileNameLabel) {
                fileNameLabel.textContent = file.name;
            }
        } else {
            setCollectTokenCsvFile(null);
        }
    };

    const [customWalletToReceiveAllFeesFromBurn, setCustomWalletToReceiveAllFeesFromBurn] = useState(0);
    const customWalletToReceiveAllFeesFromBurnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCustomWalletToReceiveAllFeesFromBurn(parseInt(event.target.value, 10));
    };

    const [burnTokenCsvFile, setBurnTokenCsvFile] = useState<File | null>(null);
    const burnTokenCsv = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file && file.type === 'text/csv') {
            setBurnTokenCsvFile(file);

            const fileNameLabel = document.querySelector('.burn-token-csv-file-name');
            if (fileNameLabel) {
                fileNameLabel.textContent = file.name;
            }
        } else {
            setBurnTokenCsvFile(null);
        }
    };

    const [thawTokenAccountsCsvFile, setThawTokenAccountsCsvFile] = useState<File | null>(null);
    const thawTokenAccountsCsv = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file && file.type === 'text/csv') {
            setThawTokenAccountsCsvFile(file);

            const fileNameLabel = document.querySelector('.thaw-token-accounts-csv-file-name');
            if (fileNameLabel) {
                fileNameLabel.textContent = file.name;
            }
        } else {
            setThawTokenAccountsCsvFile(null);
        }
    };

    const downloadCustomTokenAirdropExample = async () => csvDownloader(customTokenAirdropExample, 'customTokenAirdropExample');
    const downloadCustomTokenCollectorExample = async () => csvDownloader(customTokenCollectorExample, 'customTokenCollectorExample');

    const [skipDeployerWallet, setSkipDeployerWallet] = useState<boolean>(true);

    async function sendTokenToSingleWallet(
        connection: Connection,
        tokenMint: PublicKey,
        receiver: PublicKey,
        walletsFrom: Keypair[],
        jitoTip: number,
        funder: Keypair,
    ) {
        logMessage('Sending all tokens to single wallet...');
        if (receiver.toBase58() === PublicKey.default.toBase58()) {
            logMessage('Wallet not found!');
            return;
        }
        const bundleIds = await sendTokensFromAllWalletsToReceiver(connection, tokenMint, receiver, walletsFrom, jitoTip, funder);

        for (const bundleId of bundleIds) {
            logMessage(`bundle sent: ${bundleId}`);
        }

        logMessage(`All tokens sent to ${receiver.toBase58()}`);
    }

    async function burnAllTokenAccounts(connection: Connection, unlockedSolReceiver: PublicKey, walletsFrom: Keypair[], jitoTip: number, funder: Keypair) {
        const MAX_RETRIES = 5;
        let retryCount = 0;

        walletsFrom = removeKeypairDuplicates(walletsFrom);
        logMessage('Burning all token accounts...');
        logMessage(`Found ${walletsFrom.length} unique wallets`);
        console.log(walletsFrom.map((wallet) => wallet.publicKey.toBase58()));

        while (retryCount < MAX_RETRIES) {
            try {
                await burnAllTokenAccountsWithJito(connection, unlockedSolReceiver, walletsFrom, jitoTip, funder, logMessage);

                logMessage('All token accounts burned', 'success');
                return;
            } catch (e) {
                retryCount++;
                logMessage(`Error during burn attempt ${retryCount}: ${e}`);

                if (retryCount >= MAX_RETRIES) {
                    logMessage('Burning failed after max retries. Some tokens may still exist.', 'error');
                    return;
                }

                logMessage('Retrying burn process...');
            }
        }
    }

    async function customTokenAirdrop(connection: Connection, walletsFrom: Keypair, deployedTokenMint: string, csvFile: File) {
        logMessage('Custom token airdrop started...');
        const parsedCsv = await parseCsv(csvFile);

        const receivers: Keypair[] = [];
        const amounts: number[] = [];

        const tokenDecimals = await getTokenDecimals(connection, new PublicKey(deployedTokenMint));
        for (const row of parsedCsv) {
            const [receiver, amount] = row;
            const wallet = Keypair.fromSecretKey(new Uint8Array(bs58.decode(receiver)));

            receivers.push(wallet);
            amounts.push(parseFloat(amount) * 10 ** tokenDecimals);
        }
        logMessage('Creating token accounts');
        await createTokenAccountsWithJito(connection, receivers, new PublicKey(deployedTokenMint), 'confirmed', tokenManagerJitoTip, logMessage);
        logMessage('Token accounts created', 'success');
        await massAirdropWithJito(
            connection,
            new PublicKey(deployedTokenMint),
            walletsFrom,
            receivers.map((receiver) => receiver.publicKey),
            amounts,
            tokenManagerJitoTip,
            logMessage,
        );
        logMessage('Custom token airdrop completed', 'success');
    }

    async function customTokenCollect(connection: Connection, receiver: PublicKey, deployedTokenMint: string, csvFile: File) {
        logMessage('Custom token collect started...');
        const parsedCsv = await parseCsv(csvFile);

        const senders: Keypair[] = [];

        for (const row of parsedCsv) {
            const [sender] = row;
            const wallet = Keypair.fromSecretKey(new Uint8Array(bs58.decode(sender)));
            senders.push(wallet);
        }

        const bundleIds = await sendTokensFromAllWalletsToReceiver(
            connection,
            new PublicKey(deployedTokenMint),
            receiver,
            senders,
            tokenManagerJitoTip,
            funder,
        );

        for (const bundleId of bundleIds) {
            logMessage(`bundle sent: ${bundleId}`);
        }

        logMessage('All bundles confirmed', 'success');
    }

    async function customTokenAccountBurn(
        connection: Connection,
        unlockedSolReceiver: PublicKey,
        file: File,
        funder: Keypair,
        mint: string,
        chooseTokenToBurn: string,
    ) {
        logMessage('Burning token accounts...');
        const parsedCsv = await parseCsv(file);

        const wallets: Keypair[] = [];
        for (const row of parsedCsv) {
            const [wallet] = row;
            const keyPair = Keypair.fromSecretKey(new Uint8Array(bs58.decode(wallet)));
            wallets.push(keyPair);
        }

        for (let i = 0; i < 5; i++) {
            try {
                if (chooseTokenToBurn === 'All Tokens') {
                    await burnAllTokenAccountsWithJito(connection, unlockedSolReceiver, wallets, tokenManagerJitoTip, funder, logMessage);
                } else if (chooseTokenToBurn === 'Current Token') {
                    await burnAllTokenAccountsWithJito(connection, unlockedSolReceiver, wallets, tokenManagerJitoTip, funder, logMessage, new PublicKey(mint));
                }
                break;
            } catch (error) {
                console.error('error', error);
                await sleep(1000);
                logMessage(`Error burning tokens ${error}. Starting again`, 'error');
            }
            if (i === 4) {
                return logMessage('Too many fails. Please try again', 'error');
            }
        }

        logMessage('All bundles sent', 'success');
    }

    async function thawTokenAccounts(connection: Connection, csvFile: File) {
        //try {
        logMessage('Thawing token accounts...');
        const lines = await parseCsv(csvFile);
        const wallets: PublicKey[] = [];
        for (const line of lines) {
            const walletPublicOrPrivateKey = line[0];

            let wallet: PublicKey;
            try {
                wallet = new PublicKey(walletPublicOrPrivateKey);
            } catch (e) {
                const keypair = Keypair.fromSecretKey(new Uint8Array(bs58.decode(walletPublicOrPrivateKey)));
                wallet = keypair.publicKey;
            }
            wallets.push(wallet);
        }

        logMessage(`Found ${wallets.length} wallets`);

        const allTokenAccounts: PublicKey[] = [];
        const allMints: PublicKey[] = [];

        let tokenAccountsWithRevokedFreeze = 0;
        let tokenAccountsWithWrongFreezeAuthority = 0;
        let tokenAccountsWithCorrectFreezer = 0;
        let notFrozenTokenAccounts = 0;

        for (const wallet of wallets) {
            const tokenAccounts = await getWalletTokenAccounts(connection, wallet);

            //slice accounts for 100 per slice
            for (let i = 0; i < tokenAccounts.length; i += 100) {
                const slicedTokenAccounts = tokenAccounts.slice(i, i + 100);
                const accounts = await getMultipleAccounts(
                    connection,
                    slicedTokenAccounts.map((tokenAccount) => tokenAccount.pubkey),
                    'processed',
                );
                const mints = await getMultipleMints(
                    connection,
                    slicedTokenAccounts.map((tokenAccount) => tokenAccount.accountInfo.mint),
                );

                for (let i = 0; i < mints.length; i++) {
                    if (accounts[i].isFrozen) {
                        if (mints[i].freezeAuthority === null) {
                            tokenAccountsWithRevokedFreeze++;
                        } else if (mints[i].freezeAuthority!.toBase58() === freezer.publicKey.toBase58()) {
                            allTokenAccounts.push(accounts[i].address);
                            allMints.push(mints[i].address);
                            tokenAccountsWithCorrectFreezer++;
                        } else {
                            console.log('freezeAuthority', mints[i].freezeAuthority!.toBase58());
                            tokenAccountsWithWrongFreezeAuthority++;
                        }
                    } else {
                        notFrozenTokenAccounts++;
                    }
                }
            }
        }

        logMessage(
            `Found ${
                tokenAccountsWithRevokedFreeze + tokenAccountsWithWrongFreezeAuthority + tokenAccountsWithCorrectFreezer + notFrozenTokenAccounts
            } token accounts`,
        );
        logMessage(`Including ${notFrozenTokenAccounts} not frozen token accounts`);
        logMessage(`Including ${tokenAccountsWithRevokedFreeze} token accounts without freeze authority`);
        logMessage(`Including ${tokenAccountsWithWrongFreezeAuthority} token accounts with a wrong freezer`);
        logMessage(`Including ${tokenAccountsWithCorrectFreezer} token accounts with a correct freezer`);
        await thawTokenAccountsWithJito(connection, freezer, allTokenAccounts, allMints, tokenManagerJitoTip, logMessage);

        logMessage('All token accounts thawed', 'success');
        // } catch (error) {
        //     logMessage(`Error thawing tokens ${error}`, 'error');
        // }
    }

    return (
        <section className='token-manager-csv-wrap'>
            <h4 className='token-manager-csv-title'>Token - Using Loaded CSV</h4>
            <section className='token-option-container'>
                <div className='token-option-wrap'>
                    <div className='token-option-header'>
                        <b>Collect Specified Token Into Single Wallet</b>
                        <p className='tooltip'>?</p>
                        <div className='tooltip-text'>PLACEHOLDER TEXT #1</div>
                    </div>
                    <div className='token-option-description'>
                        <p>Target Wallet To Receive All Tokens</p>
                        <select className='token-manager-csv-select' value={walletToReceiveAllTokens} onChange={walletToReceiveAllTokensChange}>
                            {allWallets.map((_, index) => (
                                <option value={index} key={index}>
                                    {`${selectedWalletRoles[index]} - ${selectedWalletLabels[index]}`}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => {
                                sendTokenToSingleWallet(
                                    connection,
                                    new PublicKey(deployedTokenMint),
                                    allWallets[walletToReceiveAllTokens].publicKey,
                                    allUniqueWallets,
                                    tokenManagerJitoTip,
                                    funder,
                                );
                            }}
                            disabled={!deployedTokenMint}
                        >
                            Collect All Tokens
                        </button>

                        <button
                            onClick={() => {
                                sendTokenToSingleWallet(
                                    connection,
                                    new PublicKey(deployedTokenMint),
                                    allWallets[walletToReceiveAllTokens].publicKey,
                                    removeKeypairDuplicates(allUniqueWallets.concat(volumeBuyWallets)),
                                    tokenManagerJitoTip,
                                    funder,
                                );
                            }}
                            disabled={!deployedTokenMint}
                        >
                            Collect All Tokens (With Volume)
                        </button>
                    </div>
                </div>
                <div className='token-option-wrap'>
                    <div className='token-option-header'>
                        <b>Burn All Token Accounts (with retries)</b>
                    </div>
                    <div className='token-option-description'>
                        <p>Target Wallet To Receive All Fees From Burn</p>
                        <select className='token-manager-csv-select' value={walletToReceiveAllFeesFromBurn} onChange={walletToReceiveAllFeesFromBurnChange}>
                            {allWallets.map((_, index) => (
                                <option value={index} key={index}>
                                    {`${selectedWalletRoles[index]} - ${selectedWalletLabels[index]}`}
                                </option>
                            ))}
                        </select>
                        <label className='burn-confirmation'>
                            <input type='checkbox' checked={burnConfirmation} onChange={burnConfirmationChagne} />
                            YOU MUST CHECK TO PROCEED
                        </label>
                        <label className='burn-confirmation'>
                            <input
                                type='checkbox'
                                checked={skipDeployerWallet}
                                onChange={(e) => {
                                    setSkipDeployerWallet((prev) => !prev);
                                }}
                            />
                            SKIP DEPLOYER
                        </label>
                        <button
                            onClick={() => {
                                burnAllTokenAccounts(
                                    connection,
                                    allWallets[walletToReceiveAllFeesFromBurn].publicKey,
                                    skipDeployerWallet ? allUniqueWallets.filter((wallet) => wallet !== deployer) : allUniqueWallets,
                                    tokenManagerJitoTip,
                                    deployer,
                                );
                            }}
                            disabled={!burnConfirmation}
                            className='button-danger'
                        >
                            BURN
                        </button>
                        <button
                            onClick={() => {
                                logMessage('Burning all user volume accumulators...');
                                burnAllUserVolumeAccumulators(
                                    connection,
                                    skipDeployerWallet ? allUniqueWallets.filter((wallet) => wallet !== deployer) : allUniqueWallets,
                                    burnerPriorityFee,
                                    logMessage,
                                );
                            }}
                            disabled={!burnConfirmation}
                            className='button-danger button'
                        >
                            BURN PUMPSWAP VOLUME ACC
                        </button>
                    </div>
                </div>
            </section>
            <h4 className='token-manager-csv-title'>Token - Using Custom CSV</h4>
            <section className='token-option-container'>
                <div className='token-option-wrap'>
                    <div className='token-option-header'>
                        <b>Custom CSV - Airdrop Token</b>
                        <button className='ex-file-button' onClick={downloadCustomTokenAirdropExample}>
                            Ex. File
                        </button>
                        <p className='tooltip'>?</p>
                        <div className='tooltip-text'>PLACEHOLDER TEXT #2</div>
                    </div>
                    <div className='token-option-csv'>
                        <label className='token-option-csv-label' htmlFor='disperseTokenFileInput'>
                            <span className='token-option-csv-svg'></span>
                            <span className='csv-file-name disperse-token-csv-file-name'>Upload CSV with wallet addresses to disperse to</span>
                        </label>
                        <input type='file' accept='.csv' id='disperseTokenFileInput' onChange={disperseTokenCsv} />
                    </div>
                    <div className='token-option-description'>
                        <p>Source Wallet of Token</p>
                        <select className='token-manager-csv-select' value={sourceWalletOfToken} onChange={sourceWalletOfTokenChange}>
                            {allWallets.map((_, index) => (
                                <option value={index} key={index}>
                                    {`${selectedWalletRoles[index]} - ${selectedWalletLabels[index]}`}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => {
                                customTokenAirdrop(connection, allWallets[sourceWalletOfToken], deployedTokenMint, disperseTokenCsvFile!);
                            }}
                            disabled={!deployedTokenMint || !disperseTokenCsvFile}
                        >
                            Airdrop/Disperce Token
                        </button>
                    </div>
                </div>
                <div className='token-option-wrap'>
                    <div className='token-option-header'>
                        <b>Custom CSV - Collect Token</b>
                        <button className='ex-file-button' onClick={downloadCustomTokenCollectorExample}>
                            Ex. File
                        </button>
                        <p className='tooltip'>?</p>
                        <div className='tooltip-text'>PLACEHOLDER TEXT #3</div>
                    </div>
                    <div className='token-option-csv'>
                        <label className='token-option-csv-label' htmlFor='collectTokenFileInput'>
                            <span className='token-option-csv-svg'></span>
                            <span className='csv-file-name collect-token-csv-file-name'>Upload CSV with private keys to collect token</span>
                        </label>
                        <input type='file' accept='.csv' id='collectTokenFileInput' onChange={collectTokenCsv} />
                    </div>
                    <div className='token-option-description'>
                        <p>Target Wallet to Receive All Tokens</p>
                        <select className='token-manager-csv-select' value={customWalletToReceiveAllTokens} onChange={customWalletToReceiveAllTokensChange}>
                            {allWallets.map((_, index) => (
                                <option value={index} key={index}>
                                    {`${selectedWalletRoles[index]} - ${selectedWalletLabels[index]}`}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() =>
                                customTokenCollect(connection, allWallets[customWalletToReceiveAllTokens].publicKey, deployedTokenMint, collectTokenCsvFile!)
                            }
                            disabled={!deployedTokenMint || !collectTokenCsvFile}
                        >
                            Airdrop/Disperce Token
                        </button>
                    </div>
                </div>
                <div className='token-option-wrap'>
                    <div className='token-option-header'>
                        <b>Custom CSV - Burn Tokens</b>
                        <button className='ex-file-button'>Ex. File</button>
                        <p className='tooltip'>?</p>
                        <div className='tooltip-text'>PLACEHOLDER TEXT #4</div>
                    </div>
                    <div className='token-option-csv'>
                        <label className='token-option-csv-label' htmlFor='burnTokenFileInput'>
                            <span className='token-option-csv-svg'></span>
                            <span className='csv-file-name burn-token-csv-file-name'>Upload CSV with private keys to burn</span>
                        </label>
                        <input type='file' accept='.csv' id='burnTokenFileInput' onChange={burnTokenCsv} />
                    </div>
                    <div className='token-option-radio'>
                        <label>
                            <input
                                type='radio'
                                name='token'
                                value='Current Token'
                                checked={chooseTokenToBurn === 'Current Token'}
                                onChange={chooseTokenToBurnChange}
                            />
                            <span>Current Token</span>
                        </label>
                        <label>
                            <input
                                type='radio'
                                name='token'
                                value='All Tokens'
                                checked={chooseTokenToBurn === 'All Tokens'}
                                onChange={chooseTokenToBurnChange}
                            />
                            <span>All Tokens</span>
                        </label>
                    </div>
                    <div className='token-option-description'>
                        <p>Target Wallet To Receive All Fees From Burn</p>
                        <select
                            className='token-manager-csv-select'
                            value={customWalletToReceiveAllFeesFromBurn}
                            onChange={customWalletToReceiveAllFeesFromBurnChange}
                        >
                            {allWallets.map((_, index) => (
                                <option value={index} key={index}>
                                    {`${selectedWalletRoles[index]} - ${selectedWalletLabels[index]}`}
                                </option>
                            ))}
                        </select>
                        <label className='burn-confirmation'>
                            <input type='checkbox' checked={customBurnConfirmation} onChange={customBurnConfirmationChange} />
                            YOU MUST CHECK TO PROCEED
                        </label>
                        <button
                            onClick={() => {
                                customTokenAccountBurn(
                                    connection,
                                    allWallets[customWalletToReceiveAllFeesFromBurn].publicKey,
                                    burnTokenCsvFile!,
                                    allWallets[customWalletToReceiveAllFeesFromBurn],
                                    deployedTokenMint,
                                    chooseTokenToBurn,
                                );
                            }}
                            className='button-danger'
                            disabled={!burnTokenCsvFile || !customBurnConfirmation}
                        >
                            BURN
                        </button>
                    </div>
                </div>
                <div className='token-option-wrap'>
                    <div className='token-option-header'>
                        <b>Custom CSV - Thaw Accounts</b>
                        <button className='ex-file-button'>Ex. File</button>
                        <p className='tooltip'>?</p>
                        <div className='tooltip-text'>PLACEHOLDER TEXT #5</div>
                    </div>
                    <div className='token-option-csv'>
                        <label className='token-option-csv-label' htmlFor='thawTokenAccountsFileInput'>
                            <span className='token-option-csv-svg'></span>
                            <span className='csv-file-name thaw-token-accounts-csv-file-name'>Upload CSV with token accounts to thaw</span>
                        </label>
                        <input type='file' accept='.csv' id='thawTokenAccountsFileInput' onChange={thawTokenAccountsCsv} />
                    </div>
                    <div className='token-option-description'>
                        <button
                            onClick={() => {
                                thawTokenAccounts(connection, thawTokenAccountsCsvFile!);
                            }}
                            disabled={!thawTokenAccountsCsvFile || !freezer}
                        >
                            Thaw Token Accounts
                        </button>
                    </div>
                </div>
            </section>
        </section>
    );
}
